import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'

import { DatePicker } from '../../lib/DatePicker'
import {
  STATUS_LABEL,
  displayName,
  errorText,
  initial,
  lookupLabel,
  optionsFor,
  useEmployees,
} from '../../lib/employees'
import type {
  Approver,
  Employee,
  EmployeePayload,
  EmployeeRow,
  Lookups,
} from '../../lib/employees'
import { canEdit } from '../../lib/nav'
import { Pager } from '../../lib/Pager'
import { SortBar, SortTh, useSort } from '../../lib/Sort'
import { useAuthedSession } from '../../lib/SessionContext'
import { SectionHead, ViewerNote } from '../offsite/parts'

/**
 * ข้อมูลพนักงาน — ตาราง + แผงเลื่อนออกด้านข้างสำหรับดู/แก้
 *
 * ⚠️ หน้านี้เขียนลงตาราง Users ของ HRMOnline ซึ่งเป็นของ HR ที่ระบบอื่นใช้ร่วมกัน
 *    และไม่มีฐานทดสอบ — กติกาเต็มอยู่หัวไฟล์ backend/src/lib/employees.js
 *
 * กติกาที่หน้าจอต้องรักษาไว้ให้ได้ (ไม่ใช่แค่ backend):
 *
 *  1) **ส่งเฉพาะช่องที่แก้จริง** — dirtyPayload() เทียบกับค่าที่โหลดมา แล้วส่งแค่
 *     ส่วนต่าง ถ้าส่งทั้งฟอร์มทุกครั้ง ช่องที่หน้านี้ยังไม่ได้วาด (หรือวาดแล้วแต่
 *     โหลดมาไม่ครบ) จะถูกเขียนทับด้วยค่าว่างโดยไม่มีใครสั่ง
 *  2) **ตัวเลือกต้องมีค่าปัจจุบันเสมอ** — optionsFor() ยัดค่าที่คนนี้ถืออยู่เข้าไป
 *     แม้จะเป็นรหัสที่ไม่มีชื่อในตารางกลางแล้ว ไม่งั้น <select> จะเด้งไปค่าแรกเอง
 *     แล้วกดบันทึกทีเดียวแผนกเปลี่ยนโดยไม่ได้ตั้งใจ
 *  3) **เตือนก่อนปิดแผงถ้ายังไม่บันทึก** — แผงปิดง่ายมาก (Esc / กดฉากหลัง)
 */

type FormState = Record<string, string | boolean | null>

/** ค่าที่ฟอร์มถืออยู่ → ค่าที่ backend รับ (ช่องว่าง = null ไม่ใช่ '') */
function toPayloadValue(value: string | boolean | null): string | boolean | number | null {
  if (typeof value === 'boolean') return value
  if (value === null) return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/** ค่าจาก API → ค่าที่ input ใช้ได้ (input ไม่รับ null) */
function toFormValue(value: unknown): string | boolean | null {
  if (typeof value === 'boolean') return value
  if (value === null || value === undefined) return ''
  return String(value)
}

export default function Employees() {
  const session = useAuthedSession()
  const employees = useEmployees()
  const writable = canEdit(session)

  const [rows, setRows] = useState<EmployeeRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const [q, setQ] = useState('')
  /** บริษัทที่กำลังดูอยู่ — ว่างได้เฉพาะช่วงก่อน lookups มาถึงเท่านั้น */
  const [companyCode, setCompanyCode] = useState('')
  const [status, setStatus] = useState<'active' | 'resigned' | 'all'>('active')
  const [departCode, setDepartCode] = useState('')
  const [branchCode, setBranchCode] = useState('')
  const [functionCode, setFunctionCode] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const sort = useSort([{ key: 'status', dir: 'asc' }, { key: 'empCode', dir: 'asc' }], () => setPage(1))

  const [lookups, setLookups] = useState<Lookups | null>(null)
  /** 'new' = ฟอร์มเพิ่มคน · string = รหัสที่เปิดอยู่ · null = ปิดแผง */
  const [open, setOpen] = useState<string | 'new' | null>(null)

  /* ค้นหาแล้วต้องกลับหน้า 1 — ไม่งั้นค้างอยู่หน้า 8 ของผลเดิมแล้วเห็นตารางว่าง
     ทั้งที่มีผลลัพธ์ (เคยเป็นกับดักคลาสสิกของตารางที่มีทั้งค้นหาและแบ่งหน้า) */
  const filters = useMemo(
    () => ({ q: q.trim(), status, departCode, branchCode, functionCode, companyCode }),
    [q, status, departCode, branchCode, functionCode, companyCode],
  )
  const firstRender = useRef(true)
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    setPage(1)
  }, [filters])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await employees.list({ ...filters, page, pageSize, sort: sort.param })
      setRows(res.employees)
      setTotal(res.total)
      setLoadError(null)
    } catch (err) {
      setLoadError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [employees, filters, page, pageSize, sort.param])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * ดาวน์โหลดทะเบียนพนักงานเป็น CSV — แทน "รายงานข้อมูลพนักงาน" ของระบบเดิม
   *
   * ⚠️ ต้องวนขอทีละหน้า เพราะ API จำกัด pageSize ไว้ที่ 100 (lib/employees.js
   *    parsePaging) ถ้าส่ง pageSize ใหญ่ ๆ ไปจะถูกหั่นเหลือ 100 เงียบ ๆ แล้วได้
   *    ไฟล์ที่ขาดคนไปโดยไม่มีอะไรเตือน
   *
   * ใช้ตัวกรองเดียวกับที่เห็นบนจอ — คนดาวน์โหลดคาดหวังว่าได้สิ่งที่กรองไว้
   */
  const exportCsv = async () => {
    setExporting(true)
    try {
      const SIZE = 100
      const all: EmployeeRow[] = []
      for (let p = 1; ; p += 1) {
        const res = await employees.list({ ...filters, page: p, pageSize: SIZE, sort: sort.param })
        all.push(...res.employees)
        if (all.length >= res.total || res.employees.length === 0) break
        if (p > 200) break // กันวนไม่รู้จบถ้า total เพี้ยน
      }

      const head = [
        'รหัสพนักงาน', 'คำนำหน้า', 'ชื่อ', 'นามสกุล', 'ชื่อเต็ม', 'เพศ',
        'บริษัท', 'รหัสสาขา', 'สาขา (จังหวัด)', 'รหัสฝ่าย', 'ฝ่าย', 'รหัสแผนก', 'แผนก', 'BU', 'ตำแหน่ง',
        'วันเริ่มงาน', 'วันลาออก', 'สถานะ', 'คิดเวลาทำงาน', 'ทำ OT ได้',
      ]
      const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
      const body = all.map((r) =>
        [
          r.empCode, r.prename, r.firstName, r.lastName, r.fullName, r.sex,
          r.companyCode, r.branchCode, r.branchName, r.functionCode, r.functionName,
          r.departCode, r.departName,
          r.buCode, r.position, r.workStartDate, r.resignDate,
          r.status === 'active' ? 'ทำงานอยู่' : r.status === 'resigned' ? 'ลาออก' : r.status,
          r.processTa ? 'ใช่' : 'ไม่', r.canOt ? 'ใช่' : 'ไม่',
        ]
          .map(esc)
          .join(','),
      )
      // BOM ไม่งั้น Excel เปิดมาภาษาไทยเป็นตัวต่างด้าว
      const blob = new Blob(['﻿' + [head.map(esc).join(','), ...body].join('\r\n')], {
        type: 'text/csv;charset=utf-8',
      })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `ทะเบียนพนักงาน_${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(a.href)
      setNotice(`ดาวน์โหลดแล้ว ${all.length.toLocaleString('th-TH')} คน`)
    } catch (err) {
      setLoadError(errorText(err))
    } finally {
      setExporting(false)
    }
  }

  // ตัวเลือกโหลดครั้งเดียว — ไม่เปลี่ยนระหว่างใช้งานหน้านี้
  useEffect(() => {
    employees
      .lookups()
      .then((l) => {
        setLookups(l)
        /* ต้องเจาะบริษัทเสมอ (ไม่มีตัวเลือก "ทุกบริษัท") — ตั้งตัวแรกให้ทันที
           ที่รู้ว่ามีบริษัทอะไรบ้าง ไม่งั้น companyCode ค้างเป็น '' แล้วรายชื่อ
           จะกลายเป็นของทุกบริษัทรวมกัน ซึ่งตรงข้ามกับที่ตั้งใจ */
        setCompanyCode((cur) => cur || (l.companyCodes[0] ?? ''))
      })
      .catch((err) => setLoadError(errorText(err)))
  }, [employees])

  /* แผนก/สาขาในแถบค้นหา ต้องกรองตามบริษัทที่เลือกอยู่ — ไม่งั้นจะเห็นแผนกของ
     ทุกบริษัทปนกัน แล้วเลือกแผนกของ SM07 ขณะกรองบริษัท SM01 จะได้ตารางว่าง
     โดยไม่มีอะไรบอกว่าทำไม

     lookups ส่ง companyCode มาต่อแต่ละตัวเลือกอยู่แล้ว จึงกรองที่หน้าจอได้เลย
     ไม่ต้องยิง API ซ้ำตอนสลับบริษัท

     ⚠️ ตัวที่ companyCode เป็น null ปล่อยผ่าน — เป็นรหัสที่มีใน Users แต่ไม่มี
        ในทะเบียน ถ้ากรองทิ้งจะหาคนกลุ่มนั้นไม่เจอเลย (ทะเบียนไม่ครบเป็นปัญหา
        ที่มีอยู่จริงในฐานนี้) */
  const inCompany = useCallback(
    (item: { companyCode: string | null }) =>
      !companyCode || item.companyCode === null || item.companyCode === companyCode,
    [companyCode],
  )

  const departmentChoices = useMemo(
    () =>
      lookups
        ? optionsFor(lookups.departments, null).filter((d) => (d.inUse || d.known) && inCompany(d))
        : [],
    [lookups, inCompany],
  )
  const branchChoices = useMemo(
    () =>
      lookups
        ? optionsFor(lookups.branches, null).filter((b) => (b.inUse || b.known) && inCompany(b))
        : [],
    [lookups, inCompany],
  )
  const functionChoices = useMemo(
    () =>
      lookups
        ? optionsFor(lookups.functions, null).filter((f) => (f.inUse || f.known) && inCompany(f))
        : [],
    [lookups, inCompany],
  )

  return (
    <>
      <h1>พนักงาน</h1>
      {!writable && <ViewerNote />}
      {loadError && <p className="error">โหลดข้อมูลไม่สำเร็จ — {loadError}</p>}
      {notice && <p className="ok">{notice}</p>}

      <div className="emp-toolbar">
        {/* ซ้ายสุด: เลือกบริษัทก่อน แล้วค่อยค้น/กรองอย่างอื่นภายในบริษัทนั้น
            ⚠️ ไม่มีตัวเลือก "ทุกบริษัท" — ต้องเจาะบริษัทเสมอ ค่าเริ่มต้นเป็นตัวแรก
               ของ hrCompanyCodes ที่ตั้งใน useEffect ข้างล่าง */}
        {(lookups?.companyCodes.length ?? 0) > 0 ? (
          <select
            value={companyCode}
            onChange={(e) => {
              setCompanyCode(e.target.value)
              /* ล้างแผนก/สาขาที่ค้างอยู่ — ค่าเดิมเป็นของบริษัทก่อนหน้า ถ้าไม่ล้าง
                 จะกรองด้วยรหัสที่ไม่มีในบริษัทใหม่แล้วได้ตารางว่างโดยไม่รู้สาเหตุ */
              setDepartCode('')
              setBranchCode('')
              setFunctionCode('')
            }}
            aria-label="บริษัท"
          >
            {(lookups?.companyCodes ?? []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        ) : null}

        <input
          className="emp-search"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ค้นด้วยรหัส หรือชื่อ–สกุล (ไทย/อังกฤษ)"
          aria-label="ค้นหาพนักงาน"
        />

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          aria-label="สถานะ"
        >
          <option value="active">ทำงาน</option>
          <option value="resigned">ลาออก</option>
          <option value="all">ทั้งหมด</option>
        </select>

        <select
          value={branchCode}
          onChange={(e) => setBranchCode(e.target.value)}
          aria-label="สาขา (จังหวัด)"
        >
          <option value="">ทุกจังหวัด</option>
          {branchChoices.map((b) => (
            <option key={b.code} value={b.code}>
              {lookupLabel(b)}
            </option>
          ))}
        </select>

        <select
          value={functionCode}
          onChange={(e) => setFunctionCode(e.target.value)}
          aria-label="ฝ่าย (function)"
        >
          <option value="">ทุกฝ่าย</option>
          {functionChoices.map((f) => (
            <option key={f.code} value={f.code}>
              {lookupLabel(f)}
            </option>
          ))}
        </select>

        <select
          value={departCode}
          onChange={(e) => setDepartCode(e.target.value)}
          aria-label="แผนก"
        >
          <option value="">ทุกแผนก</option>
          {departmentChoices.map((d) => (
            <option key={d.code} value={d.code}>
              {lookupLabel(d)}
            </option>
          ))}
        </select>

        {/* viewer ก็ดาวน์โหลดได้ — เป็นข้อมูลที่เห็นบนจออยู่แล้ว ไม่ใช่การแก้ */}
        <button type="button" className="ghost" onClick={() => void exportCsv()} disabled={exporting}>
          {exporting ? 'กำลังรวบรวม…' : 'ดาวน์โหลด CSV'}
        </button>
        {writable && (
          <button type="button" onClick={() => setOpen('new')}>
            เพิ่มพนักงาน
          </button>
        )}
      </div>

      <section className="card">
        <SectionHead
          title="รายชื่อ"
          meta={
            rows === null
              ? undefined
              : `${total.toLocaleString('th-TH')} คน${status === 'active' ? ' ที่ทำงานอยู่' : ''}`
          }
          onReload={() => void load()}
          loading={loading}
        />

        {rows !== null && rows.length === 0 && (
          <p className="meta">ไม่พบพนักงานที่ตรงกับเงื่อนไข</p>
        )}

        {rows !== null && rows.length > 0 && (
          <>
            <SortBar
              sort={sort.sort}
              labels={{
                empCode: 'พนักงาน',
                department: 'แผนก',
                position: 'ตำแหน่ง',
                hireDate: 'เริ่มงาน',
                status: 'สถานะ',
              }}
              onClear={() => sort.setSort([])}
            />
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <SortTh label="พนักงาน" sortKey="empCode" sort={sort.sort} onToggle={sort.toggle} />
                    <SortTh label="บริษัท" sortKey="company" sort={sort.sort} onToggle={sort.toggle} />
                    {/* สาขา = จังหวัด (BranchName) · ฝ่าย = function (FunctionName) — join อีกตาราง เรียงตาม code */}
                    <th>สาขา (จังหวัด)</th>
                    <th>ฝ่าย</th>
                    <SortTh label="แผนก" sortKey="department" sort={sort.sort} onToggle={sort.toggle} />
                    <SortTh label="ตำแหน่ง" sortKey="position" sort={sort.sort} onToggle={sort.toggle} />
                    <SortTh label="เริ่มงาน" sortKey="hireDate" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                    <SortTh label="สถานะ" sortKey="status" sort={sort.sort} onToggle={sort.toggle} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.empCode}
                      className={open === row.empCode ? 'emp-row--selected' : undefined}
                    >
                      <td>
                        {/* ทั้งแถวควรกดได้ แต่ต้องเป็นปุ่มจริงเพื่อให้ Tab ถึงและ Enter ทำงาน
                            (onClick บน <tr> เข้าถึงด้วยคีย์บอร์ดไม่ได้) */}
                        <button
                          type="button"
                          className="link-cell"
                          onClick={() => setOpen(row.empCode)}
                        >
                          <span className="emp-avatar" aria-hidden="true">
                            {initial(row)}
                          </span>
                          <span>
                            <span className="emp-name">{displayName(row)}</span>
                            <span className="emp-code">{row.empCode}</span>
                          </span>
                        </button>
                      </td>
                      <td>{row.companyCode ?? '—'}</td>
                      <td>{row.branchName ?? row.branchCode ?? '—'}</td>
                      <td>{row.functionName ?? row.functionCode ?? '—'}</td>
                      <td>{row.departName ?? row.departCode ?? '—'}</td>
                      <td>{row.position ?? '—'}</td>
                      <td className="nowrap">{row.workStartDate ?? '—'}</td>
                      <td>
                        <span
                          className={
                            row.status === 'active'
                              ? 'chip chip--ok'
                              : row.status === 'resigned'
                                ? 'chip chip--off'
                                : 'chip chip--warn'
                          }
                        >
                          {STATUS_LABEL[row.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager
              page={page}
              pageSize={pageSize}
              total={total}
              loading={loading}
              onPage={setPage}
              onPageSize={(size) => {
                setPageSize(size)
                setPage(1)
              }}
              unit="คน"
            />
          </>
        )}
      </section>

      {open !== null && lookups && (
        <EmployeeDrawer
          // key บังคับให้ state ในแผงเริ่มใหม่เมื่อสลับไปดูอีกคน
          key={open}
          empCode={open === 'new' ? null : open}
          lookups={lookups}
          writable={writable}
          onClose={() => setOpen(null)}
          onSaved={(message) => {
            setOpen(null)
            setNotice(message)
            void load()
          }}
        />
      )}
    </>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
   แผงเลื่อนออกด้านข้าง
   ──────────────────────────────────────────────────────────────────────────── */

function EmployeeDrawer({
  empCode,
  lookups,
  writable,
  onClose,
  onSaved,
}: {
  /** null = ฟอร์มเพิ่มคนใหม่ */
  empCode: string | null
  lookups: Lookups
  writable: boolean
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const employees = useEmployees()
  const creating = empCode === null

  const [loaded, setLoaded] = useState<Employee | null>(null)
  const [form, setForm] = useState<FormState>({})
  const [loading, setLoading] = useState(!creating)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (creating) {
      setForm({ processTa: true, canOt: true, disabled: false, disabledPerson: false })
      return
    }
    let alive = true
    setLoading(true)
    employees
      .get(empCode)
      .then((employee) => {
        if (!alive) return
        setLoaded(employee)
        setForm(Object.fromEntries(Object.entries(employee).map(([k, v]) => [k, toFormValue(v)])))
        setError(null)
      })
      .catch((err) => alive && setError(errorText(err)))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [employees, empCode, creating])

  const set = useCallback(
    (key: string) => (value: string | boolean | null) =>
      setForm((prev) => ({ ...prev, [key]: value })),
    [],
  )

  /**
   * ส่วนต่างระหว่างฟอร์มกับค่าที่โหลดมา — ส่งเฉพาะที่แก้จริง
   * (เหตุผลอยู่หัวไฟล์ ข้อ 1 — ห้ามเปลี่ยนเป็นส่งทั้งฟอร์ม)
   */
  const changed = useMemo(() => {
    if (creating) return form
    if (!loaded) return {}
    const diff: FormState = {}
    for (const [key, value] of Object.entries(form)) {
      if (toFormValue((loaded as unknown as Record<string, unknown>)[key]) !== value) {
        diff[key] = value
      }
    }
    return diff
  }, [creating, form, loaded])

  const dirty = Object.keys(changed).length > 0

  const close = useCallback(() => {
    if (dirty && !window.confirm('ยังไม่ได้บันทึก — ปิดแล้วสิ่งที่แก้จะหายไป ปิดเลยไหม')) return
    onClose()
  }, [dirty, onClose])

  // Esc ปิดแผง — ผูกที่ document เพราะโฟกัสอาจอยู่ที่ช่องไหนก็ได้ในแผง
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [close])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy || !writable) return

    setBusy(true)
    setError(null)
    try {
      const payload = Object.fromEntries(
        Object.entries(changed)
          // ช่องพวกนี้ backend เป็นคนเติมเอง ส่งไปจะโดนปฏิเสธด้วย "ฟิลด์ที่ไม่รู้จัก"
          .filter(([key]) => !DERIVED.has(key))
          .map(([key, value]) => [key, toPayloadValue(value)]),
      ) as EmployeePayload

      if (creating) {
        const made = await employees.create(payload)
        onSaved(`เพิ่มพนักงาน ${made.empCode} แล้ว`)
      } else {
        const saved = await employees.update(empCode, payload)
        onSaved(`บันทึกข้อมูล ${displayName(saved)} แล้ว`)
      }
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const title = creating ? 'เพิ่มพนักงานใหม่' : loaded ? displayName(loaded) : (empCode ?? '')
  const canSeePassword = writable && (creating || (loaded !== null && 'password' in loaded))

  return (
    <>
      {/* ฉากหลังต้อง unmount จริงตอนปิด ห้ามซ่อนด้วย display:none ค้างไว้ —
          index.css ใช้ body:has(.drawer-scrim) ล็อกการเลื่อนหน้าข้างหลัง
          ถ้าค้างอยู่ใน DOM ทั้งหน้าจะเลื่อนไม่ได้ถาวร */}
      <button type="button" className="drawer-scrim" onClick={close} aria-label="ปิดแผง" />

      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <header className="drawer-head">
          <div>
            <p className="drawer-title">{title}</p>
            <p className="drawer-sub">
              {creating
                ? 'กรอกรหัสพนักงานเองตามกติกาของ HR'
                : `${loaded?.empCode ?? empCode} · ${loaded?.departName ?? loaded?.departCode ?? 'ไม่ระบุแผนก'}`}
            </p>
          </div>
          <button type="button" className="drawer-close" onClick={close} aria-label="ปิด">
            ✕
          </button>
        </header>

        {/* emp-form = ชุดขนาดหนาแน่นเฉพาะฟอร์มนี้ (ดู index.css) — ฟอร์มอื่นในระบบ
            ใช้ขนาดปกติ ห้ามย้ายคลาสนี้ไปไว้ที่ .drawer เพราะแผงอื่นก็ใช้ .drawer */}
        <form className="emp-form" onSubmit={submit}>
          <div className="drawer-body">
            {loading && <p className="meta">กำลังโหลด…</p>}
            {error && <p className="error">{error}</p>}

            {!loading && (
              <>
                <Group title="ตัวตน">
                  {creating ? (
                    <Field label="รหัสพนักงาน">
                      <input
                        value={String(form.empCode ?? '')}
                        onChange={(e) => set('empCode')(e.target.value.toUpperCase())}
                        maxLength={10}
                        pattern="[A-Za-z0-9]+"
                        required
                        autoFocus
                      />
                    </Field>
                  ) : (
                    <Field label="รหัสพนักงาน">
                      <input
                        value={String(form.empCode ?? loaded?.empCode ?? '')}
                        onChange={(e) => set('empCode')(e.target.value)}
                        maxLength={10}
                      />
                    </Field>
                  )}
                </Group>

                <Group title="ข้อมูลบุคคล">
                  <Field label="คำนำหน้า">
                    <select
                      value={String(form.prename ?? '')}
                      onChange={(e) => set('prename')(e.target.value)}
                    >
                      <option value="">— ไม่ระบุ —</option>
                      {withCurrent(lookups.prenames, form.prename).map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="เพศ">
                    <select
                      value={String(form.sex ?? '')}
                      onChange={(e) => set('sex')(e.target.value)}
                    >
                      <option value="">— ไม่ระบุ —</option>
                      {withCurrent(lookups.sexes, form.sex).map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Text label="ชื่อ" value={form.firstName} onChange={set('firstName')} max={30} />
                  <Text label="นามสกุล" value={form.lastName} onChange={set('lastName')} max={30} />
                  <Text
                    label="ชื่อ (อังกฤษ)"
                    value={form.firstNameEng}
                    onChange={set('firstNameEng')}
                    max={100}
                  />
                  {/* ไม่มีช่อง "นามสกุล (อังกฤษ)" โดยตั้งใจ — ของจริงในคอลัมน์
                      LastName_Eng ไม่ใช่นามสกุล แต่เป็นชื่อเล่นไทย (เบิร์ด นุช กี้)
                      มีอยู่ 229 จาก 1684 คน ป้ายเดิมทำให้คนกรอกผิดประเภทซ้ำเข้าไปอีก
                      ซ่อนแค่ในฟอร์ม — ค่าใน DB, API, ช่องค้นหา และหน้ารายงานยังคงเดิม
                      ทั้งหมด ไม่ได้ลบข้อมูลของใคร */}
                  <DateField label="วันเกิด" value={form.birthDate} onChange={set('birthDate')} />
                  <Text
                    label="เลขบัตรประชาชน"
                    value={form.citizenId}
                    onChange={set('citizenId')}
                    max={20}
                    note="ข้อมูลจริงมีทั้งแบบ 13 หลักล้วนและแบบมีขีด — ระบบลงทะเบียน LINE ตัดขีดออกให้เองตอนเทียบ"
                  />
                  {/* อยู่ติดเลขบัตรประชาชนเพราะเป็น "เลขบัตร" เหมือนกัน คนกรอกมัก
                      หยิบบัตรทั้งสองใบมาพร้อมกัน — เดิมอยู่คนละกลุ่มห่างกันครึ่งฟอร์ม */}
                  <Text
                    label="เลขบัตรตอกเวลา"
                    value={form.mcCard}
                    onChange={set('mcCard')}
                    max={10}
                    note="ระบบลงเวลาอ่านค่านี้สด ๆ ทุกครั้ง เปลี่ยนแล้วมีผลทันที · ห้ามซ้ำกับคนอื่น"
                  />
                </Group>

                <Group title="ติดต่อ">
                  <Text label="อีเมล" value={form.email} onChange={set('email')} max={50} type="email" />
                  <Text label="เบอร์โทร" value={form.phoneNo} onChange={set('phoneNo')} max={50} />
                </Group>

                <Group title="สังกัด">
                  <Field label="บริษัท">
                    <select
                      value={String(form.companyCode ?? '')}
                      onChange={(e) => set('companyCode')(e.target.value)}
                    >
                      <option value="">— ไม่ระบุ —</option>
                      {withCurrent(lookups.companyCodes, form.companyCode).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <small className="field-note">
                      เลือกได้เฉพาะบริษัทที่บัญชีนี้ดูแล — ย้ายออกนอกขอบเขตแล้วจะมองไม่เห็นคนนี้อีก
                    </small>
                  </Field>
                  <LookupField
                    label="สาขา (จังหวัด)"
                    items={lookups.branches}
                    value={form.branchCode}
                    onChange={set('branchCode')}
                  />
                  <LookupField
                    label="ฝ่าย (function)"
                    items={lookups.functions}
                    value={form.functionCode}
                    onChange={set('functionCode')}
                  />
                  <LookupField
                    label="แผนก"
                    items={lookups.departments}
                    value={form.departCode}
                    onChange={set('departCode')}
                  />
                  <Field label="ระดับ (BU)">
                    <select
                      value={String(form.buCode ?? '')}
                      onChange={(e) => set('buCode')(e.target.value)}
                    >
                      <option value="">— ไม่ระบุ —</option>
                      {lookups.buLevels.map((b) => (
                        <option key={b.code} value={b.code}>
                          {b.code} · {b.name ?? '—'}
                        </option>
                      ))}
                      {/* ค่าเดิมที่ไม่อยู่ในรายการ (ของจริงมี 'SM01-01' ที่กรอกผิดช่อง)
                          ต้องมีให้เห็น ไม่งั้น select จะเด้งไปค่าอื่นแล้วบันทึกทับ */}
                      {form.buCode &&
                        !lookups.buLevels.some((b) => b.code === form.buCode) && (
                          <option value={String(form.buCode)}>
                            {String(form.buCode)} (ค่าเดิม ไม่อยู่ในรายการมาตรฐาน)
                          </option>
                        )}
                    </select>
                  </Field>
                  <Field label="ตำแหน่ง" full>
                    <input
                      value={String(form.position ?? '')}
                      onChange={(e) => set('position')(e.target.value)}
                      maxLength={100}
                      list="employee-positions"
                    />
                    {/* พิมพ์เองได้ แต่มีของเดิมให้เลือก — 344 แบบ มากเกินจะเป็น dropdown
                        แต่ถ้าให้พิมพ์เปล่า ๆ ตำแหน่งเดียวกันจะสะกดไม่ตรงกันจนกรองไม่ได้ */}
                    <datalist id="employee-positions">
                      {lookups.positions.map((p) => (
                        <option key={p} value={p} />
                      ))}
                    </datalist>
                  </Field>
                </Group>

                <Group title="การจ้าง">
                  <DateField
                    label="วันเริ่มงาน"
                    value={form.workStartDate}
                    onChange={set('workStartDate')}
                  />
                  <DateField
                    label="วันลาออก"
                    value={form.resignDate}
                    onChange={set('resignDate')}
                    note="ใส่วันที่แล้วคนนี้จะหายจากรายชื่อ “ทำงาน” ทันที และลงทะเบียน LINE ไม่ได้"
                  />
                  <DateField
                    label="วันครบทดลองงาน"
                    value={form.probationDate}
                    onChange={set('probationDate')}
                  />
                  <Field label="จำนวนวันทดลองงาน">
                    <input
                      type="number"
                      min={0}
                      max={3650}
                      value={String(form.probationDay ?? '')}
                      onChange={(e) => set('probationDay')(e.target.value)}
                    />
                  </Field>
                </Group>

                <Group title="ผู้อนุมัติ" even>
                  <ApproverField
                    label="ผู้อนุมัติลา"
                    code={form.leaveApproveId}
                    name={loaded?.leaveApproveName ?? null}
                    onChange={set('leaveApproveId')}
                  />
                  <ApproverField
                    label="ผู้อนุมัติลำดับ 2"
                    code={form.approve2}
                    name={loaded?.approve2Name ?? null}
                    onChange={set('approve2')}
                  />
                  <ApproverField
                    label="ผู้อนุมัติลำดับ 3"
                    code={form.approve3}
                    name={loaded?.approve3Name ?? null}
                    onChange={set('approve3')}
                  />
                </Group>

                <Group title="บัตรและสถานะ">
                  <Text label="ไฟล์รูป" value={form.photo} onChange={set('photo')} max={50} />
                  <Toggle
                    label="ปิดใช้งานบัญชี"
                    value={form.disabled}
                    onChange={set('disabled')}
                  />
                  <Toggle
                    label="ปิดใช้งาน (บุคคล)"
                    value={form.disabledPerson}
                    onChange={set('disabledPerson')}
                  />
                  <Toggle label="คิดเวลาทำงาน" value={form.processTa} onChange={set('processTa')} />
                  <Toggle label="ทำ OT ได้" value={form.canOt} onChange={set('canOt')} />
                </Group>

                {canSeePassword && (
                  <Group title="รหัสผ่านระบบ HR">
                    <Field label="รหัสผ่าน" full>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={String(form.password ?? '')}
                        onChange={(e) => set('password')(e.target.value)}
                        maxLength={30}
                        autoComplete="new-password"
                      />
                      <small className="field-note">
                        ⚠️ ระบบ HR เดิมเก็บรหัสผ่านเป็นข้อความธรรมดา ช่องนี้จึงอ่านค่าเดิมได้
                        — ไม่ใช่ความสามารถที่ตั้งใจเพิ่ม แต่เป็นสภาพของข้อมูลที่มีอยู่
                        (บัญชีสิทธิ์ “ดูอย่างเดียว” จะไม่เห็นช่องนี้)
                      </small>
                    </Field>

                    {/* ⚠️ ต้องอยู่ **นอก** <Field> — Field คือ <label> อยู่แล้ว
                        การเอา <label> มาซ้อน <label> ผิดสเปก HTML และเบราว์เซอร์จะ
                        ส่งคลิกไปที่ช่องรหัสผ่านแทนที่จะติ๊ก checkbox */}
                    <label className="field-grid--full">
                      <input
                        type="checkbox"
                        checked={showPassword}
                        onChange={(e) => setShowPassword(e.target.checked)}
                      />
                      แสดงรหัสผ่าน
                    </label>
                  </Group>
                )}
              </>
            )}
          </div>

          <footer className="drawer-foot">
            {writable ? (
              <>
                <button type="submit" disabled={busy || loading || !dirty}>
                  {busy ? 'กำลังบันทึก…' : creating ? 'เพิ่มพนักงาน' : 'บันทึก'}
                </button>
                <button type="button" className="ghost" onClick={close} disabled={busy}>
                  ยกเลิก
                </button>
                <span className="meta">
                  {dirty ? `แก้ไว้ ${Object.keys(changed).length} ช่อง` : 'ยังไม่ได้แก้อะไร'}
                </span>
              </>
            ) : (
              <button type="button" className="ghost" onClick={close}>
                ปิด
              </button>
            )}
          </footer>
        </form>
      </aside>
    </>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
   ชิ้นส่วนของฟอร์ม
   ──────────────────────────────────────────────────────────────────────────── */

/** คอลัมน์ที่ backend เติมเอง — ส่งไปจะโดนปฏิเสธด้วย "ฟิลด์ที่ไม่รู้จัก" */
const DERIVED = new Set([
  'userId', 'empId', 'username', 'fullName', 'status',
  'departName', 'branchName', 'buDesc',
  'leaveApproveName', 'approve2Name', 'approve3Name',
])

/** ค่าปัจจุบันต้องอยู่ในรายการเสมอ ไม่งั้น select เด้งไปค่าอื่นแล้วบันทึกทับ */
function withCurrent(list: string[], current: string | boolean | null): string[] {
  const value = typeof current === 'string' ? current.trim() : ''
  if (!value || list.includes(value)) return list
  return [...list, value]
}

function Group({
  title,
  children,
  even,
}: {
  title: string
  children: ReactNode
  /** แบ่งความกว้างเต็มแถวเท่า ๆ กัน แทนการเรียงตามคอลัมน์มาตรฐาน */
  even?: boolean
}) {
  return (
    <>
      <p className="fieldset-title">{title}</p>
      <div className={even ? 'field-grid field-grid--even' : 'field-grid'}>{children}</div>
    </>
  )
}

function Field({
  label,
  full,
  children,
}: {
  label: string
  full?: boolean
  children: ReactNode
}) {
  return (
    <label className={full ? 'field-grid--full' : undefined}>
      {label}
      {children}
    </label>
  )
}

function Text({
  label,
  value,
  onChange,
  max,
  note,
  type = 'text',
}: {
  label: string
  value: string | boolean | null | undefined
  onChange: (value: string) => void
  max: number
  note?: string
  type?: string
}) {
  return (
    <Field label={label}>
      <input
        type={type}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        maxLength={max}
      />
      {note && <small className="field-note">{note}</small>}
    </Field>
  )
}

function DateField({
  label,
  value,
  onChange,
  note,
}: {
  label: string
  value: string | boolean | null | undefined
  onChange: (value: string) => void
  note?: string
}) {
  return (
    <Field label={label}>
      <DatePicker value={String(value ?? '')} onChange={onChange} allowEmpty />
      {note && <small className="field-note">{note}</small>}
    </Field>
  )
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string
  value: string | boolean | null | undefined
  onChange: (value: boolean) => void
}) {
  return (
    <label>
      <input
        type="checkbox"
        checked={value === true}
        onChange={(e) => onChange(e.target.checked)}
      />{' '}
      {label}
    </label>
  )
}

function LookupField({
  label,
  items,
  value,
  onChange,
}: {
  label: string
  items: Lookups['departments']
  value: string | boolean | null | undefined
  onChange: (value: string) => void
}) {
  const current = typeof value === 'string' && value ? value : null
  const choices = useMemo(() => optionsFor(items, current), [items, current])

  return (
    <Field label={label}>
      <select value={current ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">— ไม่ระบุ —</option>
        {choices.map((item) => (
          <option key={item.code} value={item.code}>
            {lookupLabel(item)}
          </option>
        ))}
      </select>
    </Field>
  )
}

/**
 * ช่องเลือกผู้อนุมัติ — ค้นจากรายชื่อพนักงานจริง
 *
 * ไม่ให้พิมพ์รหัสเปล่า ๆ เพราะรหัสที่ไม่มีตัวตนจะทำให้ใบลาวิ่งไปหาคนที่ไม่มีอยู่
 * (backend ปฏิเสธซ้ำอีกชั้น แต่ควรรู้ตั้งแต่ก่อนกดบันทึก)
 */
function ApproverField({
  label,
  code,
  name,
  onChange,
}: {
  label: string
  code: string | boolean | null | undefined
  /** ชื่อที่ backend เก็บคู่กับรหัส — ใช้โชว์ค่าเดิมโดยไม่ต้องยิง API เพิ่ม */
  name: string | null
  onChange: (value: string) => void
}) {
  const employees = useEmployees()
  const [term, setTerm] = useState('')
  const [list, setList] = useState<Approver[] | null>(null)
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  const selected = typeof code === 'string' && code ? code : ''

  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      employees
        .approvers(term)
        .then(setList)
        .catch(() => setList([]))
    }, 200)
    return () => clearTimeout(id)
  }, [employees, term, open])

  // กดที่อื่นแล้วปิดรายการ — ไม่งั้นรายการค้างทับเนื้อหาข้างล่าง
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <Field label={label}>
      <div className="combo" ref={box}>
        <input
          value={open ? term : selected ? `${selected}${name ? ` · ${name}` : ''}` : ''}
          onChange={(e) => setTerm(e.target.value)}
          onFocus={() => {
            setTerm('')
            setOpen(true)
          }}
          placeholder="พิมพ์รหัสหรือชื่อเพื่อค้น"
        />
        {open && (
          <ul className="combo-list">
            <li>
              <button
                type="button"
                className="combo-item"
                onClick={() => {
                  onChange('')
                  setOpen(false)
                }}
              >
                — ไม่ระบุ —
              </button>
            </li>
            {list === null && <li className="combo-item">กำลังค้น…</li>}
            {list?.length === 0 && <li className="combo-item">ไม่พบพนักงานที่ตรงกับคำค้น</li>}
            {list?.map((a) => (
              <li key={a.code}>
                <button
                  type="button"
                  className={a.code === selected ? 'combo-item combo-item--active' : 'combo-item'}
                  onClick={() => {
                    onChange(a.code)
                    setOpen(false)
                  }}
                >
                  {a.code} · {a.name ?? '—'}
                  {/* ผู้อนุมัติที่ลาออกแล้วยังมีอยู่ในข้อมูลเดิม จึงเลือกได้ แต่ต้องเห็นว่าลาออก */}
                  {a.resigned && <span className="chip chip--off">ลาออก</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Field>
  )
}
